document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-kit-stepper]').forEach((root) => {
    initProductKitStepper(root);
  });
});

const initProductKitStepper = (root) => {
  const productInfo = root.closest('[product-info]');
  const panels = Array.from(root.querySelectorAll('[data-step-panel]'));
  const triggers = Array.from(root.querySelectorAll('[data-step-trigger]'));
  const components = parseJsonData(root, '[data-kit-components-json]');
  const kitVariants = parseJsonData(root, '[data-kit-variants-json]');
  const kitOptions = parseJsonData(root, '[data-kit-options-json]');
  const debug = isKitDebugEnabled(root);
  const sectionId = root.dataset.sectionId;
  const formId = root.dataset.formId || `product-form-${sectionId}`;
  const isInclusionFlow = root.dataset.kitFlow === 'inclusion';
  const stepRequiresChoice = panels.map((panel) => panel.dataset.requiresChoice === 'true');
  const hasRequiredChoices = stepRequiresChoice.some(Boolean);
  const resolvedSteps = stepRequiresChoice.map((requiresChoice) => !requiresChoice);
  const selections = new Array(panels.length).fill(null);
  const feedbackTimers = new WeakMap();
  let currentStep = 0;
  let shouldSyncMainMedia = false;

  if (!panels.length) return;

  productInfo?.classList.add('is-kit-stepper-ready');
  moveThemeBuyForm(root, productInfo);

  if (debug) debugKitInit({ root, components, kitOptions, kitVariants });

  const getProgressState = () => {
    const completedCount = resolvedSteps.filter(Boolean).length;
    const nextStep = getNextActionStep(0);

    return {
      allResolved: completedCount === panels.length,
      completedCount,
      nextStep,
    };
  };

  const getNextActionStep = (startIndex = 0) => {
    const normalizedStart = Math.max(0, Math.min(startIndex, panels.length - 1));

    for (let index = normalizedStart; index < panels.length; index += 1) {
      if (stepRequiresChoice[index] && !resolvedSteps[index]) return index;
    }

    for (let index = 0; index < normalizedStart; index += 1) {
      if (stepRequiresChoice[index] && !resolvedSteps[index]) return index;
    }

    return -1;
  };

  const getKitState = () => {
    const allStepsSelected = selections.every(Boolean);
    const allSelectionsAvailable = selections.every((selection) => selection?.available);
    const progressState = getProgressState();
    const match = allStepsSelected && allSelectionsAvailable
      ? hasRequiredChoices
        ? findSelectedKitVariant({ selections, kitVariants, kitOptions, components, debug })
        : findDefaultKitVariant(kitVariants)
      : null;
    const kitVariant = match?.variant || null;

    if (!allStepsSelected) {
      return {
        canAdd: false,
        message: '',
        buttonText: root.dataset.incompleteText || 'Selecione as opções',
        variant: null,
        match,
      };
    }

    if (!progressState.allResolved) {
      return {
        canAdd: false,
        message: '',
        buttonText: root.dataset.incompleteText || 'Selecione as opções',
        variant: null,
        match,
      };
    }

    if (!allSelectionsAvailable) {
      return {
        canAdd: false,
        message: root.dataset.soldOutText || 'Produto indisponível',
        buttonText: root.dataset.soldOutText || 'Produto indisponível',
        variant: null,
        match,
      };
    }

    if (!kitVariant) {
      const unavailableText = hasRequiredChoices
        ? root.dataset.combinationUnavailableText || 'Combinação indisponível'
        : root.dataset.soldOutText || 'Produto indisponível';

      return {
        canAdd: false,
        message: unavailableText,
        buttonText: unavailableText,
        variant: null,
        match,
      };
    }

    if (!kitVariant.available) {
      return {
        canAdd: false,
        message: root.dataset.soldOutText || 'Produto indisponível',
        buttonText: root.dataset.soldOutText || 'Produto indisponível',
        variant: kitVariant,
        match,
      };
    }

    return {
      canAdd: true,
      message: '',
      buttonText: root.dataset.addText || window.variantStrings?.addToCart || 'Adicionar ao carrinho',
      variant: kitVariant,
      match,
    };
  };

  const getStepMedia = (index) => {
    const selection = selections[index];
    const component = components[index] || {};

    return {
      src: selection?.image || selection?.fallbackImage || component.featuredImage || root.dataset.fallbackImage || '',
      alt: selection?.imageAlt || selection?.fallbackImageAlt || component.featuredImageAlt || selection?.productTitle || component.title || '',
    };
  };

  const syncMainMediaForStep = (index) => {
    updateMainMedia(getStepMedia(index), productInfo);
  };

  const enableMainMediaSync = () => {
    shouldSyncMainMedia = true;
  };

  const showStep = (index) => {
    const hasActiveStep = index >= 0 && index < panels.length;
    if (!hasActiveStep && !(isInclusionFlow && index === -1)) return;

    if (isInclusionFlow) {
      panels.forEach((panel) => {
        const active = hasActiveStep && Number(panel.dataset.stepPanel) === index;
        const panelIndex = Number(panel.dataset.stepPanel);
        const complete = resolvedSteps[panelIndex];
        panel.hidden = false;
        panel.classList.toggle('is-active', active);
        panel.classList.toggle('is-complete', complete);
        panel.classList.toggle('is-pending', !active && !complete);
      });

      triggers.forEach((trigger) => {
        const triggerIndex = Number(trigger.dataset.stepTrigger);
        const active = hasActiveStep && triggerIndex === index;

        trigger.classList.toggle('is-active', active);
        trigger.classList.toggle('is-complete', resolvedSteps[triggerIndex]);
        trigger.disabled = false;
        trigger.setAttribute('aria-current', active ? 'true' : 'false');
      });

      currentStep = index;
      if (hasActiveStep && shouldSyncMainMedia) syncMainMediaForStep(index);
      return;
    }

    panels.forEach((panel) => {
      const active = Number(panel.dataset.stepPanel) === index;
      const panelIndex = Number(panel.dataset.stepPanel);
      const complete = resolvedSteps[panelIndex];
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
      panel.classList.toggle('is-complete', complete);
      panel.classList.toggle('is-pending', !active && !complete);
    });

    triggers.forEach((trigger) => {
      const triggerIndex = Number(trigger.dataset.stepTrigger);
      const active = triggerIndex === index;
      const available = canOpenStep(triggerIndex);

      trigger.classList.toggle('is-active', active);
      trigger.classList.toggle('is-complete', resolvedSteps[triggerIndex]);
      trigger.disabled = !available;
      trigger.setAttribute('aria-current', active ? 'step' : 'false');
    });

    currentStep = index;
    if (shouldSyncMainMedia) syncMainMediaForStep(index);
  };

  const canOpenStep = (index) => {
    if (isInclusionFlow) return true;
    if (index <= currentStep) return true;
    return selections.slice(0, index).every((selection) => selection?.available);
  };

  const updatePanelButtons = () => {
    panels.forEach((panel, index) => {
      const nextButton = panel.querySelector('[data-step-next]');
      if (!nextButton) return;

      nextButton.disabled = !selections[index]?.available;
      nextButton.textContent = index === panels.length - 1 ? 'Concluir kit' : 'Próximo passo';
    });
  };

  const updateProgressDisplay = () => {
    const progressState = getProgressState();
    const progressLabel = root.querySelector('[data-kit-progress-label]');
    const progressAction = root.querySelector('[data-kit-progress-action]');
    const activePanel = panels[currentStep];

    root.classList.toggle('is-kit-complete', progressState.allResolved);

    panels.forEach((panel, index) => {
      const status = panel.querySelector('[data-step-status]');
      const trigger = panel.querySelector('[data-step-trigger]');
      const complete = resolvedSteps[index];
      const active = index === currentStep;

      if (status) {
        status.textContent = complete ? 'Concluído' : active ? 'Agora' : 'Pendente';
      }

      if (trigger) {
        trigger.setAttribute('aria-expanded', active ? 'true' : 'false');
      }
    });

    if (progressLabel) {
      progressLabel.textContent = progressState.allResolved
        ? 'Kit completo'
        : activePanel?.dataset.stepLabel || `Passo ${currentStep + 1} de ${panels.length}`;
    }

    if (progressAction) {
      progressAction.textContent = progressState.allResolved
        ? 'Revise suas escolhas e adicione o kit ao carrinho.'
        : activePanel?.dataset.stepPrompt || 'Selecione uma opção para continuar.';
    }
  };

  const syncPanelSelection = (panel, selection) => {
    if (!selection) return null;

    const selectedLabel = panel?.querySelector('[data-selected-value]');

    selections[selection.componentIndex] = selection;
    if (selectedLabel) selectedLabel.textContent = selection.colorName;
    updateStepImage(panel, selection, root.dataset.fallbackImage);

    return selection;
  };

  const applyInputSelection = (input) => {
    const selection = getSelectionFromInput(input, components);
    return syncPanelSelection(input.closest('[data-step-panel]'), selection);
  };

  const confirmPanelSelection = (panel) => {
    if (!panel) return;

    const previousTimer = feedbackTimers.get(panel);
    if (previousTimer) window.clearTimeout(previousTimer);

    panel.classList.remove('is-selection-confirmed');
    void panel.offsetWidth;
    panel.classList.add('is-selection-confirmed');

    const timer = window.setTimeout(() => {
      panel.classList.remove('is-selection-confirmed');
      feedbackTimers.delete(panel);
    }, 900);
    feedbackTimers.set(panel, timer);
  };

  const applyDefaultSelections = () => {
    panels.forEach((panel, index) => {
      const inputs = Array.from(panel.querySelectorAll('[data-kit-variant-input]'));

      // Passos com escolha de cor começam sem nenhuma cor marcada: o cliente escolhe.
      if (stepRequiresChoice[index]) {
        inputs.forEach((item) => { item.checked = false; });
        const selectedLabel = panel.querySelector('[data-selected-value]');
        if (selectedLabel) selectedLabel.textContent = 'escolha uma cor';
        return;
      }

      const input = (
        inputs.find((item) => item.checked && !item.disabled) ||
        inputs.find((item) => !item.disabled) ||
        inputs[0]
      );

      if (input) {
        input.checked = true;
        const selection = applyInputSelection(input);
        if (stepRequiresChoice[index] && selection?.available) {
          resolvedSteps[index] = true;
        }
        return;
      }

      syncPanelSelection(panel, getSelectionFromComponent(components[index], index));
    });
  };

  const handleInputSelection = (input) => {
    const selection = applyInputSelection(input);
    if (!selection) return;

    if (stepRequiresChoice[selection.componentIndex]) {
      resolvedSteps[selection.componentIndex] = selection.available === true;
    }
    currentStep = selection.componentIndex;
    enableMainMediaSync();
    confirmPanelSelection(input.closest('[data-step-panel]'));
    syncMainMediaForStep(selection.componentIndex);
    updateStepperState();
  };

  const completeStep = (index) => {
    if (!Number.isInteger(index) || index < 0 || index >= panels.length) return;
    if (stepRequiresChoice[index] && !selections[index]?.available) return;

    resolvedSteps[index] = true;
    currentStep = index + 1 < panels.length ? index + 1 : -1;
    updateStepperState();
  };

  const updateStepperState = () => {
    const kitState = getKitState();

    updatePanelButtons();
    updateVariantInputs(kitState.variant, sectionId, formId);
    syncThemeButtons(root, kitState, sectionId, formId);

    if (kitState.message) {
      showKitMessage(root, kitState.message);
    } else {
      hideKitMessage(root);
    }

    showStep(currentStep);
    updateProgressDisplay();
  };

  const handleKitSubmit = (event) => {
    const kitState = getKitState();
    if (kitState.canAdd) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    showKitMessage(root, kitState.message || root.dataset.incompleteText || 'Selecione as opções');
    updateStepperState();
  };

  getProductForms(sectionId, formId).forEach((form) => {
    form.addEventListener('submit', handleKitSubmit, true);
  });

  root.addEventListener('change', (event) => {
    const input = event.target.closest('[data-kit-variant-input]');
    if (!input) return;

    handleInputSelection(input);
  });

  root.addEventListener('click', (event) => {
    const optionLabel = event.target.closest('[data-kit-option] label');
    const nextButton = event.target.closest('[data-step-next]');
    const prevButton = event.target.closest('[data-step-prev]');
    const trigger = event.target.closest('[data-step-trigger]');

    if (optionLabel) {
      const optionInput = document.getElementById(optionLabel.getAttribute('for'));
      if (optionInput?.matches('[data-kit-variant-input]') && root.contains(optionInput) && optionInput.checked) {
        handleInputSelection(optionInput);
      }
      return;
    }

    if (nextButton) {
      enableMainMediaSync();
      const panel = nextButton.closest('[data-step-panel]');
      completeStep(Number(panel?.dataset.stepPanel));
      return;
    }

    if (prevButton) {
      enableMainMediaSync();
      showStep(currentStep - 1);
      return;
    }

    if (trigger) {
      const nextStep = Number(trigger.dataset.stepTrigger);
      if (canOpenStep(nextStep)) {
        enableMainMediaSync();
        showStep(nextStep);
      }
    }
  });

  productInfo?.querySelector('[data-product-kit-media]')?.addEventListener('click', (event) => {
    const thumb = event.target.closest('[data-kit-thumb]');
    if (!thumb) return;

    updateMainImage(productInfo, {
      src: thumb.dataset.imageSrc,
      mediaId: thumb.dataset.mediaId,
      alt: thumb.getAttribute('aria-label'),
    });
  });

  applyDefaultSelections();
  const firstChoiceStep = stepRequiresChoice.indexOf(true);
  const nextActionStep = getNextActionStep(0);
  // Open the first color-choice step expanded by default (no "próximo passo" required),
  // falling back to the next unresolved step or the first panel.
  currentStep = firstChoiceStep !== -1
    ? firstChoiceStep
    : nextActionStep !== -1
      ? nextActionStep
      : 0;
  updateStepperState();
};

const moveThemeBuyForm = (root, productInfo) => {
  const slot = root.querySelector('[data-kit-buy-slot]');
  const buyWrapper = productInfo?.querySelector('.product-quantity-buy-wrapper');

  if (!slot || !buyWrapper || slot.contains(buyWrapper)) return;

  slot.appendChild(buyWrapper);
};

const parseJsonData = (root, selector) => {
  try {
    return JSON.parse(root.querySelector(selector)?.textContent || '[]');
  } catch (error) {
    console.warn(error);
    return [];
  }
};

const getSelectionFromInput = (input, components) => {
  const componentIndex = Number(input.dataset.componentIndex);
  const variantId = Number(input.dataset.variantId);
  const payloadVariant = components[componentIndex]?.variants?.find((variant) => (
    Number(variant.id) === variantId
  ));

  return {
    componentIndex,
    productId: Number(input.dataset.productId),
    productHandle: input.dataset.productHandle || '',
    productTitle: input.dataset.productTitle || '',
    variantId,
    variantTitle: input.dataset.variantTitle || payloadVariant?.title || '',
    colorName: input.dataset.colorName || payloadVariant?.color || input.dataset.variantTitle || '',
    available: input.dataset.variantAvailable === 'true' && payloadVariant?.available !== false,
    image: input.dataset.variantImage || payloadVariant?.image || '',
    imageAlt: input.dataset.variantImageAlt || payloadVariant?.imageAlt || input.dataset.productTitle || '',
    fallbackImage: input.dataset.componentImage || components[componentIndex]?.featuredImage || '',
    fallbackImageAlt: input.dataset.componentImageAlt || components[componentIndex]?.featuredImageAlt || input.dataset.productTitle || '',
  };
};

const getSelectionFromComponent = (component, componentIndex) => {
  const variant = component?.variants?.find((item) => item.available) || component?.variants?.[0];
  if (!component || !variant) return null;

  return {
    componentIndex,
    productId: Number(component.id),
    productHandle: component.handle || '',
    productTitle: component.title || '',
    variantId: Number(variant.id),
    variantTitle: variant.title || '',
    colorName: variant.color || variant.title || component.title || '',
    available: variant.available !== false,
    image: variant.image || '',
    imageAlt: variant.imageAlt || component.title || '',
    fallbackImage: component.featuredImage || '',
    fallbackImageAlt: component.featuredImageAlt || component.title || '',
  };
};

const findSelectedKitVariant = ({ selections, kitVariants, kitOptions, components, debug }) => {
  const parentOptions = getRealParentOptions(kitOptions);
  const parentOptionCount = getParentOptionCount(parentOptions, kitVariants);
  const attempts = [];

  const optionMappedValues = getOptionMappedSelectedValues(selections, parentOptions);
  if (optionMappedValues) {
    attempts.push({
      strategy: 'kit-option-name-order',
      values: optionMappedValues,
      reason: 'Selections ordered by matching parent kit option names to component product titles/handles.',
    });
  }

  const variableStepValues = getVariableStepSelectedValues(selections, components, parentOptionCount);
  if (variableStepValues.length === parentOptionCount) {
    attempts.push({
      strategy: 'variable-step-order',
      values: variableStepValues,
      reason: 'Fixed/base-only components ignored because the parent kit has fewer option axes than kit steps.',
    });
  }

  const directValues = selections
    .slice(0, parentOptionCount)
    .map((selection) => selection.colorName);
  if (directValues.length === parentOptionCount) {
    attempts.push({
      strategy: 'step-order-parent-option-count',
      values: directValues,
      reason: 'Selections compared by step order, limited to the number of real parent kit option axes.',
    });
  }

  for (const attempt of attempts) {
    const variant = findVariantByOrderedValues(kitVariants, attempt.values);
    if (variant) {
      if (debug) debugKitMatch({ selections, kitOptions, kitVariants, attempt, variant, attempts });
      return { variant, strategy: attempt.strategy, values: attempt.values, attempts };
    }
  }

  if (debug) debugKitMatch({ selections, kitOptions, kitVariants, attempt: null, variant: null, attempts });
  return { variant: null, strategy: 'no-match', values: directValues, attempts };
};

const findDefaultKitVariant = (kitVariants) => {
  const variant = kitVariants.find((item) => item?.available) || kitVariants[0] || null;

  return {
    variant,
    strategy: variant ? 'fixed-kit-default-variant' : 'fixed-kit-no-variant',
    values: [],
    attempts: [],
  };
};

const findVariantByOrderedValues = (variants, selectedValues) => (
  variants.find((variant) => (
    selectedValues.every((selectedValue, index) => (
      normalizeOptionValue(getVariantOptionValue(variant, index)) === normalizeOptionValue(selectedValue)
    ))
  )) || null
);

const getOptionMappedSelectedValues = (selections, kitOptions) => {
  if (!kitOptions.length) return null;

  const mappedValues = kitOptions.map((kitOption) => {
    const normalizedKitOptionName = normalizeOptionName(kitOption.name);
    const matchingSelection = selections.find((selection) => {
      const normalizedProductTitle = normalizeOptionName(selection.productTitle);
      const normalizedProductHandle = normalizeOptionName(selection.productHandle);

      return (
        normalizedProductTitle === normalizedKitOptionName ||
        normalizedProductHandle === normalizedKitOptionName ||
        normalizedProductTitle.includes(normalizedKitOptionName) ||
        normalizedKitOptionName.includes(normalizedProductTitle)
      );
    });

    return matchingSelection?.colorName || null;
  });

  return mappedValues.every(Boolean) ? mappedValues : null;
};

const getVariableStepSelectedValues = (selections, components, parentOptionCount) => {
  if (!parentOptionCount || parentOptionCount >= selections.length) return [];

  const variableSelections = selections.filter((selection) => {
    const component = components[selection.componentIndex];
    return !isFixedComponent(component);
  });

  return variableSelections.slice(0, parentOptionCount).map((selection) => selection.colorName);
};

const getParentOptionCount = (kitOptions, kitVariants) => {
  if (kitOptions.length) return kitOptions.length;

  const firstVariant = kitVariants[0];
  if (!firstVariant) return 0;

  return [firstVariant.option1, firstVariant.option2, firstVariant.option3]
    .filter((value) => normalizeOptionValue(value) !== '')
    .length;
};

const getRealParentOptions = (kitOptions) => (
  kitOptions.filter((kitOption) => !isDefaultOption(kitOption))
);

const isDefaultOption = (kitOption) => {
  const values = kitOption?.values || [];
  const normalizedName = normalizeOptionValue(kitOption?.name);

  return (
    normalizedName === 'title' &&
    values.length === 1 &&
    normalizeOptionValue(values[0]) === 'default title'
  );
};

const isFixedComponent = (component) => {
  if (!component) return false;
  if ((component.variants || []).length <= 1) return true;

  const uniqueValues = new Set(
    (component.variants || [])
      .map((variant) => normalizeOptionValue(variant.color))
      .filter(Boolean)
  );

  return uniqueValues.size <= 1;
};

const normalizeOptionValue = (value) => (
  `${value ?? ''}`.trim().replace(/\s+/g, ' ').toLowerCase()
);

const normalizeOptionName = (value) => normalizeOptionValue(value)
  .replace(/\b(cor|color|tom|shade)\b/g, '')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const getVariantOptionValue = (variant, index) => {
  return variant[`option${index + 1}`];
};

const isKitDebugEnabled = (root) => {
  const params = new URLSearchParams(window.location.search);

  return (
    root.dataset.kitDebug === 'true' ||
    params.has('kit_debug') ||
    params.has('kitDebug') ||
    window.Shopify?.designMode === true
  );
};

const debugKitInit = ({ root, components, kitOptions, kitVariants }) => {
  console.groupCollapsed('[product-kit] init');
  console.info('Debug enabled for kit stepper', root);
  console.table(kitOptions.map((option, index) => ({
    index,
    optionName: option.name,
    position: option.position,
    values: option.values?.join(' | '),
  })));
  console.table(components.map((component, index) => ({
    stepIndex: index,
    componentTitle: component.title,
    componentHandle: component.handle,
    colorOptionName: component.colorOptionName,
    colorOptionPosition: component.colorOptionPosition,
    variantsCount: component.variants?.length || 0,
    fixedForParentMatch: isFixedComponent(component),
  })));
  console.table(kitVariants.map((variant) => ({
    id: variant.id,
    title: variant.title,
    option1: variant.option1,
    option2: variant.option2,
    option3: variant.option3,
    available: variant.available,
  })));
  console.groupEnd();
};

const debugKitMatch = ({ selections, kitOptions, kitVariants, attempt, variant, attempts }) => {
  console.groupCollapsed(`[product-kit] match ${variant ? 'found' : 'not found'}`);
  console.info('Selected component values in step order:');
  console.table(selections.map((selection) => ({
    stepIndex: selection.componentIndex,
    componentTitle: selection.productTitle,
    selectedValue: selection.colorName,
    normalizedValue: normalizeOptionValue(selection.colorName),
    componentVariantId: selection.variantId,
    componentAvailable: selection.available,
  })));
  console.info('Parent KIT option order:');
  console.table(kitOptions.map((option, index) => ({
    optionIndex: index,
    optionName: option.name,
    position: option.position,
    values: option.values?.join(' | '),
  })));
  console.info('Match attempts:');
  console.table(attempts.map((matchAttempt) => ({
    strategy: matchAttempt.strategy,
    selectedValuesInComparedOrder: matchAttempt.values.join(' + '),
    normalizedValues: matchAttempt.values.map(normalizeOptionValue).join(' + '),
    reason: matchAttempt.reason,
  })));
  console.info('Parent KIT variants loaded:');
  console.table(kitVariants.map((kitVariant) => ({
    id: kitVariant.id,
    title: kitVariant.title,
    option1: kitVariant.option1,
    option2: kitVariant.option2,
    option3: kitVariant.option3,
    available: kitVariant.available,
  })));

  if (variant) {
    console.info('Matched KIT variant:', {
      id: variant.id,
      title: variant.title,
      option1: variant.option1,
      option2: variant.option2,
      option3: variant.option3,
      available: variant.available,
      strategy: attempt.strategy,
    });
  } else {
    console.warn('No exact KIT variant match found. Check whether component step order matches the parent KIT option order, or whether parent KIT option names match component product titles for fallback mapping.');
  }
  console.groupEnd();
};

const updateVariantInputs = (variant, sectionId, formId) => {
  getProductForms(sectionId, formId).forEach((form) => {
    const input = form.querySelector('input[name="id"]');
    if (!input) return;

    if (!variant?.id) {
      input.disabled = true;
      input.value = '';
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    input.disabled = false;
    input.value = variant.id;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
};

const updateStepImage = (panel, selection, fallbackImage) => {
  const imageWrap = panel?.querySelector('[data-kit-step-image]');
  const src = selection?.image || selection?.fallbackImage || fallbackImage;
  if (!imageWrap || !src) return;

  let image = imageWrap.querySelector('img');
  if (!image) {
    imageWrap.innerHTML = '';
    image = document.createElement('img');
    image.className = 'product-kit-stepper__image';
    image.loading = 'lazy';
    imageWrap.appendChild(image);
  }

  image.removeAttribute('srcset');
  image.src = src;
  image.alt = selection?.imageAlt || selection?.fallbackImageAlt || '';
};

const updateMainMedia = (media, productInfo) => {
  if (!media?.src) return;

  updateMainImage(productInfo, {
    src: media.src,
    alt: media.alt,
  });
};

const updateMainImage = (productInfo, imageData) => {
  if (!productInfo || !imageData?.src) return;

  const mainImageWrap = productInfo.querySelector('[data-kit-main-image]');
  if (mainImageWrap) {
    let image = mainImageWrap.querySelector('img');
    if (!image) {
      image = document.createElement('img');
      image.className = 'product-kit-media__image image-responsive';
      mainImageWrap.appendChild(image);
    }

    setImageSource(image, imageData);
    syncKitThumbState(productInfo, imageData.src);
    return;
  }

  updateDefaultProductMedia(productInfo, imageData);
};

const updateDefaultProductMedia = (productInfo, imageData) => {
  const mediaContainer = productInfo.querySelector('[id^="product-media-container-"]');
  const carousel = mediaContainer?.querySelector('product-carousel');

  if (imageData.mediaId && carousel?.goToSlideBasedOnMedia) {
    carousel.goToSlideBasedOnMedia(imageData.mediaId);
  }

  const mainImages = [
    mediaContainer?.querySelector('.product-main-slide-container .swiper-slide:first-child [data-media-id] img.image-responsive'),
    mediaContainer?.querySelector('.product-main-slide-container [data-media-id] img.image-responsive'),
    mediaContainer?.querySelector('.product-main-slide-container img.image-responsive'),
    productInfo.querySelector('[product-grid-layout] .grid__layout__item:first-child img.image-responsive'),
  ].filter(Boolean);

  if (!mainImages.length) return;

  mainImages.forEach((image) => setImageSource(image, imageData));
  carousel?.swiperInstance?.slideTo?.(0);
};

const setImageSource = (image, imageData) => {
  image.removeAttribute('srcset');
  image.src = imageData.src;
  image.alt = imageData.alt || image.alt || '';
};

const syncKitThumbState = (productInfo, src) => {
  productInfo.querySelectorAll('[data-kit-thumb]').forEach((thumb) => {
    thumb.classList.toggle('is-active', `${thumb.dataset.imageSrc}` === `${src}`);
  });
};

const syncThemeButtons = (root, kitState, sectionId, formId) => {
  getSubmitButtons(sectionId, formId).forEach((button) => {
    button.disabled = !kitState.canAdd;
    const buttonText = button.querySelector('.btn--text');
    if (buttonText) buttonText.textContent = kitState.buttonText;
  });
};

const getSubmitButtons = (sectionId, formId) => {
  const buttons = new Set();

  getProductForms(sectionId, formId).forEach((form) => {
    const addButton = form.querySelector('[name="add"]');
    if (addButton) buttons.add(addButton);
  });

  if (formId) {
    document
      .querySelectorAll(`[add-to-cart-button][form="${formId}"]`)
      .forEach((button) => buttons.add(button));
  }

  return Array.from(buttons);
};

const getProductForms = (sectionId, formId) => {
  const forms = new Set();
  const mainForm = document.getElementById(formId);
  if (mainForm) forms.add(mainForm);

  if (sectionId) {
    document
      .querySelectorAll(`[id="product-form-${sectionId}"], [id^="product-form-${sectionId}-"]`)
      .forEach((form) => forms.add(form));
  }

  return Array.from(forms);
};

const showKitMessage = (root, message) => {
  const messageEl = root.querySelector('[data-kit-message]');
  if (!messageEl) return;

  messageEl.textContent = message;
  messageEl.hidden = false;
};

const hideKitMessage = (root) => {
  const messageEl = root.querySelector('[data-kit-message]');
  if (!messageEl) return;

  messageEl.textContent = '';
  messageEl.hidden = true;
};
